import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { createEmployeeLoan, loansApiErrorMessage } from "@/lib/hr/loansStore";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type Draft = {
  loanAmount: string;
  installmentAmount: string;
  startDate: string;
};

const emptyDraft = (): Draft => ({
  loanAmount: "",
  installmentAmount: "",
  startDate: format(new Date(), "yyyy-MM-dd"),
});

function safeNumber(v: string): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export type EmployeeLoanCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  onSaved?: () => void;
};

export function EmployeeLoanCreateDialog({ open, onOpenChange, employeeId, onSaved }: EmployeeLoanCreateDialogProps) {
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft(emptyDraft());
  }, [open, employeeId]);

  const previewInstallments = useMemo(() => {
    const loan = safeNumber(draft.loanAmount);
    const inst = safeNumber(draft.installmentAmount);
    if (!loan || !inst || loan <= 0 || inst <= 0) return null;
    return Math.max(1, Math.ceil(loan / inst));
  }, [draft.loanAmount, draft.installmentAmount]);

  const close = () => onOpenChange(false);

  const save = async () => {
    if (!employeeId || saving) return;
    const loan = safeNumber(draft.loanAmount);
    const inst = safeNumber(draft.installmentAmount);
    if (!loan || loan <= 0) return toast.error("Loan amount must be a number > 0.");
    if (!inst || inst <= 0) return toast.error("Installment amount must be a number > 0.");
    if (!draft.startDate.trim()) return toast.error("Start date is required.");

    setSaving(true);
    try {
      const result = await createEmployeeLoan({
        employeeId,
        loanAmount: draft.loanAmount,
        installmentAmount: draft.installmentAmount,
        startDate: draft.startDate,
      });
      toast.success(result.message);
      close();
      onSaved?.();
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New loan</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 py-2">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Loan amount</Label>
            <Input
              className="glass-input h-10"
              value={draft.loanAmount}
              onChange={(e) => setDraft((d) => ({ ...d, loanAmount: e.target.value }))}
              placeholder="e.g. 50000"
              inputMode="decimal"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Installment per month</Label>
            <Input
              className="glass-input h-10"
              value={draft.installmentAmount}
              onChange={(e) => setDraft((d) => ({ ...d, installmentAmount: e.target.value }))}
              placeholder="e.g. 5000"
              inputMode="decimal"
            />
            {previewInstallments ? (
              <p className="text-xs text-muted-foreground">Estimated total installments: {previewInstallments}</p>
            ) : (
              <p className="text-xs text-muted-foreground">Total installments will be calculated automatically.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              Start date <span className="text-destructive">*</span>
            </Label>
            <DatePartsPicker
              value={draft.startDate}
              onChange={(iso) => setDraft((x) => ({ ...x, startDate: iso }))}
              fromYear={1980}
              toYear={new Date().getFullYear() + 5}
              placeholder="Select start date"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="glass" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="premium" onClick={() => void save()} disabled={saving || !employeeId}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
